import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, DollarSign, Calendar, Home, ExternalLink, Image } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { CompletedJob } from "@/pages/Portfolio";

interface PortfolioListProps {
  jobs: CompletedJob[];
  onSelectJob: (job: CompletedJob) => void;
}

export function PortfolioList({ jobs, onSelectJob }: PortfolioListProps) {
  const navigate = useNavigate();

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Home className="w-12 h-12 mb-4 opacity-50" />
        <h3 className="text-lg font-medium mb-1">No completed projects yet</h3>
        <p className="text-sm">Completed jobs will appear here automatically</p>
      </div>
    );
  }

  return (
    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {jobs.map((job) => (
        <Card
          key={job.id}
          className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => onSelectJob(job)}
        >
          {/* Photo */}
          <div className="relative h-40 bg-muted">
            {job.photo_url ? (
              <img
                src={job.photo_url}
                alt={job.property_address}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Image className="w-12 h-12 text-muted-foreground/50" />
              </div>
            )}
            {job.estimated_value && (
              <Badge className="absolute top-2 right-2 bg-green-500 text-white gap-1">
                <DollarSign className="w-3 h-3" />
                {(job.estimated_value / 1000).toFixed(0)}K
              </Badge>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="font-semibold truncate mb-1">{job.owner_name}</h3>
            <p className="text-sm text-muted-foreground truncate mb-2">
              {job.property_address}
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              {job.property_city}, {job.property_state}
            </p>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {job.roof_type && (
                <Badge variant="secondary" className="text-xs">
                  {job.roof_type}
                </Badge>
              )}
              <Badge variant="outline" className="text-xs gap-1">
                <Calendar className="w-2.5 h-2.5" />
                {new Date(job.updated_at).toLocaleDateString()}
              </Badge>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/jobs/${job.id}`);
                }}
              >
                View
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  // Trigger PMI push
                }}
              >
                <MapPin className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
